export type TenantOnboardingOrgType = 'RESIDENTIAL' | 'HOME_CARE' | 'MIXED';
export type TenantOnboardingPlanTier =
  | 'ESSENTIALS'
  | 'PROFESSIONAL'
  | 'PROFESSIONAL_CLINICAL'
  | 'ENTERPRISE'
  | 'CI_FREE';

export type TenantOnboardingStepId =
  | 'welcome'
  | 'facility'
  | 'compliance-config'
  | 'key-personnel'
  | 'import-clients'
  | 'invite-team'
  | 'workforce-config'
  | 'clinical-config'
  | 'financial-config'
  | 'complete';

export interface TenantOnboardingStepScopeOptions {
  enabledModules?: readonly string[] | null;
}

type StepScope = {
  minPlan: TenantOnboardingPlanTier | null;
  orgTypes: readonly TenantOnboardingOrgType[] | null;
  relevantModules: readonly string[] | null;
};

const PLAN_TIER_ORDER: TenantOnboardingPlanTier[] = [
  'CI_FREE',
  'ESSENTIALS',
  'PROFESSIONAL',
  'PROFESSIONAL_CLINICAL',
  'ENTERPRISE',
];

export const TENANT_ONBOARDING_STEP_ORDER: readonly TenantOnboardingStepId[] = [
  'welcome',
  'facility',
  'compliance-config',
  'key-personnel',
  'import-clients',
  'invite-team',
  'workforce-config',
  'clinical-config',
  'financial-config',
  'complete',
];

const STEP_SCOPES: Record<TenantOnboardingStepId, StepScope> = {
  welcome: {
    minPlan: null,
    orgTypes: null,
    relevantModules: null,
  },
  facility: {
    minPlan: null,
    orgTypes: null,
    relevantModules: [
      '/clients',
      '/care-plans',
      '/home-care/service-areas',
      '/residential/rooms',
      '/residential/admissions',
      '/compliance/incidents',
    ],
  },
  'compliance-config': {
    minPlan: null,
    orgTypes: null,
    relevantModules: [
      '/compliance/incidents',
      '/compliance/quality-indicators',
      '/compliance/audit',
      '/compliance/governance',
      '/compliance/complaints',
      '/compliance/responsible-persons',
    ],
  },
  'key-personnel': {
    minPlan: null,
    orgTypes: null,
    relevantModules: [
      '/compliance/responsible-persons',
      '/compliance/governance',
      '/compliance/quality-indicators',
    ],
  },
  'import-clients': {
    minPlan: null,
    orgTypes: null,
    relevantModules: ['/clients', '/care-plans', '/home-care', '/residential'],
  },
  'invite-team': {
    minPlan: null,
    orgTypes: null,
    relevantModules: ['/staff', '/hr/onboarding', '/settings/users', '/settings/onboarding-packs'],
  },
  'workforce-config': {
    minPlan: 'PROFESSIONAL',
    orgTypes: null,
    relevantModules: ['/staff', '/roster', '/workforce', '/compliance/screening', '/hr/onboarding'],
  },
  'clinical-config': {
    minPlan: 'PROFESSIONAL_CLINICAL',
    orgTypes: ['RESIDENTIAL', 'MIXED'],
    relevantModules: [
      '/observations',
      '/medications',
      '/clinical',
      '/wounds',
      '/care-pathways',
      '/advance-care-plans',
    ],
  },
  'financial-config': {
    minPlan: 'PROFESSIONAL',
    orgTypes: null,
    relevantModules: [
      '/finance',
      '/finance/invoices',
      '/finance/claims',
      '/finance/contributions',
      '/finance/budgets/carry-over',
      '/home-care/budgets',
      '/home-care/funding-changes',
      '/home-care/claims',
      '/home-care/statements',
      '/cm-tasks',
    ],
  },
  complete: {
    minPlan: null,
    orgTypes: null,
    relevantModules: null,
  },
};

function planMeetsMinimum(
  plan: TenantOnboardingPlanTier,
  minPlan: TenantOnboardingPlanTier | null,
): boolean {
  if (!minPlan) return true;
  return PLAN_TIER_ORDER.indexOf(plan) >= PLAN_TIER_ORDER.indexOf(minPlan);
}

function pathMatchesModule(modulePath: string, targetPath: string): boolean {
  return (
    modulePath === targetPath ||
    modulePath.startsWith(`${targetPath}/`) ||
    targetPath.startsWith(`${modulePath}/`)
  );
}

export function isTenantOnboardingModuleRelevant(
  enabledModules: readonly string[] | null | undefined,
  relevantModules: readonly string[] | null | undefined,
): boolean {
  if (!relevantModules || relevantModules.length === 0) return true;
  if (enabledModules == null) return true;
  return relevantModules.some((targetPath) =>
    enabledModules.some((modulePath) => pathMatchesModule(modulePath, targetPath)),
  );
}

export function isTenantOnboardingStepVisible(
  stepId: TenantOnboardingStepId,
  plan: TenantOnboardingPlanTier,
  orgType: TenantOnboardingOrgType,
  options: TenantOnboardingStepScopeOptions = {},
): boolean {
  const scope = STEP_SCOPES[stepId];
  const isHomeCareFinanceStep =
    stepId === 'financial-config' && orgType !== 'RESIDENTIAL' && plan !== 'CI_FREE';

  if (!isHomeCareFinanceStep && !planMeetsMinimum(plan, scope.minPlan)) return false;
  if (scope.orgTypes && !scope.orgTypes.includes(orgType)) return false;

  return isTenantOnboardingModuleRelevant(options.enabledModules, scope.relevantModules);
}

export function getTenantOnboardingStepIdsForTenant(
  plan: TenantOnboardingPlanTier,
  orgType: TenantOnboardingOrgType,
  options: TenantOnboardingStepScopeOptions = {},
): TenantOnboardingStepId[] {
  return TENANT_ONBOARDING_STEP_ORDER.filter((stepId) =>
    isTenantOnboardingStepVisible(stepId, plan, orgType, options),
  );
}
